import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import EmptyState from '@/components/EmptyState';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';


const FormBuilder = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formTemplates, setFormTemplates] = useState([]);
  const [newForm, setNewForm] = useState({
    name: '',
    description: '',
    service_type: '',
    fields: []
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchFormTemplates();
    }
  }, [user, loading, navigate]);

  const fetchFormTemplates = async () => {
    try {
      const response = await api.get(`/form-templates`);
      setFormTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch form templates', error);
    }
  };

  const addField = () => {
    setNewForm({
      ...newForm,
      fields: [
        ...newForm.fields,
        { name: '', label: '', type: 'text', required: true, options: [] }
      ]
    });
  };

  const updateField = (index, field, value) => {
    const updatedFields = [...newForm.fields];
    updatedFields[index][field] = value;
    setNewForm({ ...newForm, fields: updatedFields });
  };

  const removeField = (index) => {
    const updatedFields = newForm.fields.filter((_, i) => i !== index);
    setNewForm({ ...newForm, fields: updatedFields });
  };

  const createFormTemplate = async () => {
    try {
      const templateData = {
        id: uuidv4(),
        name: newForm.name,
        description: newForm.description,
        service_type: newForm.service_type,
        fields: newForm.fields,
        created_at: new Date().toISOString(),
        created_by: user.id
      };

      await api.post(`/form-templates`, templateData);

      toast.success('Form template created successfully');
      setShowCreateDialog(false);
      setNewForm({ name: '', description: '', service_type: '', fields: [] });
      fetchFormTemplates();
    } catch (error) {
      console.error('Failed to create form template', error);
      toast.error('Failed to create form template');
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Form Builder"
        titleTestId="form-builder-heading"
        actions={
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-form-button">
            <Plus size={20} /> Create Form
          </Button>
        }
      />

        {formTemplates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formTemplates.map((template, index) => (
              <Card key={template.id} data-testid={`form-template-${index}`}>
                <CardHeader>
                  <CardTitle className="font-serif text-xl font-semibold text-primary">
                    {template.name}
                  </CardTitle>
                  {template.description && (
                    <p className="font-sans text-sm text-muted-foreground mt-2">
                      {template.description}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="font-sans text-sm text-muted-foreground">
                      <span className="font-medium text-primary">Service Type:</span> {template.service_type}
                    </p>
                    <p className="font-sans text-sm text-muted-foreground">
                      <span className="font-medium text-primary">Fields:</span> {template.fields.length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState message="No form templates created yet">
            <Button
              className="mt-4"
              onClick={() => setShowCreateDialog(true)}
              data-testid="create-first-form-button"
            >
              Create Your First Form
            </Button>
          </EmptyState>
        )}

      {/* Create Form Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              Create Form Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <Label htmlFor="form-name" className="text-sm font-medium mb-2 block">
                Form Name
              </Label>
              <Input
                id="form-name"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="e.g., Client Intake Form"
                data-testid="form-name-input"
              />
            </div>

            <div>
              <Label htmlFor="form-description" className="text-sm font-medium mb-2 block">
                Description
              </Label>
              <Textarea
                id="form-description"
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                placeholder="Describe the form purpose..."
                rows={2}
                data-testid="form-description-input"
              />
            </div>

            <div>
              <Label htmlFor="service-type" className="text-sm font-medium mb-2 block">
                Service Type
              </Label>
              <Input
                id="service-type"
                value={newForm.service_type}
                onChange={(e) => setNewForm({ ...newForm, service_type: e.target.value })}
                placeholder="e.g., Business Law"
                data-testid="service-type-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <Label className="text-sm font-medium">Form Fields</Label>
                <Button onClick={addField} size="sm" data-testid="add-field-button">
                  <Plus size={16} className="mr-1" /> Add Field
                </Button>
              </div>

              {newForm.fields.map((field, index) => (
                <div key={index} className="bg-secondary p-4 border border-border mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-sans text-sm font-medium text-primary">Field {index + 1}</span>
                    <Button
                      onClick={() => removeField(index)}
                      size="sm"
                      variant="destructive"
                      data-testid={`remove-field-${index}`}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <Input
                      value={field.name}
                      onChange={(e) => updateField(index, 'name', e.target.value)}
                      placeholder="Field name (e.g., email)"
                      data-testid={`field-name-${index}`}
                    />
                    <Input
                      value={field.label}
                      onChange={(e) => updateField(index, 'label', e.target.value)}
                      placeholder="Field label (e.g., Email Address)"
                      data-testid={`field-label-${index}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={field.type}
                      onValueChange={(value) => updateField(index, 'type', value)}
                    >
                      <SelectTrigger data-testid={`field-type-${index}`}>
                        <SelectValue placeholder="Field type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="tel">Phone</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="textarea">Text Area</SelectItem>
                        <SelectItem value="select">Select</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`field-required-${index}`}
                        checked={field.required}
                        onCheckedChange={(checked) => updateField(index, 'required', checked)}
                        data-testid={`field-required-${index}`}
                      />
                      <label htmlFor={`field-required-${index}`} className="font-sans text-sm font-medium cursor-pointer">Required</label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => setShowCreateDialog(false)}
                variant="outline"
                className="flex-1"
                data-testid="cancel-form-button"
              >
                Cancel
              </Button>
              <Button
                onClick={createFormTemplate}
                disabled={!newForm.name || newForm.fields.length === 0}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-form-button"
              >
                Create Form
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default FormBuilder;